import { createContext, useContext, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../services/api'

/**
 * How this VynaEMR instance is operated (from the public `/deployment-info`
 * bootstrap endpoint).
 *
 * - `saas`         — vendor-operated (FLOSTEL). Org license keys are
 *                    managed per health system.
 * - `self_managed` — customer-operated, on infrastructure they rent (their own
 *                    cloud account). Licensed by an installed file key.
 * - `on_prem`      — customer-operated, on the customer's own premises.
 *                    Licensed by an installed file key.
 *
 * The last two are commercially identical — both are customer-operated and
 * file-key licensed — and are distinguished because what the customer
 * bought is a real distinction for support and contracts.
 *
 * `demo` used to be a value here. It moved to {@link ProductEnvironment} —
 * "this is a sales demo" describes what the instance is *for*, not who operates
 * it — so the backend no longer returns it on this field.
 */
export type DeploymentMode = 'saas' | 'self_managed' | 'on_prem'

/**
 * What this instance is *for*. The second, independent axis: a vendor-run SaaS
 * has staging and demo instances, and a customer-run install has its own
 * production, staging, or evaluation (all hardened; the mode decides which
 * license purpose verifies).
 */
export type ProductEnvironment = 'production' | 'staging' | 'evaluation' | 'demo' | 'development' | 'test'

interface DeploymentModeValue {
  mode: DeploymentMode
  environment: ProductEnvironment
  /**
   * Whether this instance is licensed by an installed file key — i.e.
   * customer-operated (`self_managed` / `on_prem`). On the vendor SaaS,
   * org keys are managed per health system instead, so the file-key page
   * and nav stay hidden there.
   */
  showFileKeyLicensing: boolean
  isLoading: boolean
}

/**
 * Fail-closed: until the fetch resolves (or if it fails), treat the instance as
 * non-SaaS so a commercial surface never flashes before we know the mode.
 */
const DEFAULT: DeploymentModeValue = {
  mode: 'self_managed',
  environment: 'production',
  showFileKeyLicensing: true,
  isLoading: true,
}

const DeploymentModeContext = createContext<DeploymentModeValue>(DEFAULT)

export function DeploymentModeProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['deployment-info'],
    queryFn: async () => {
      const res = await apiClient.get<{
        deploymentMode: DeploymentMode
        environment: ProductEnvironment
      }>('/deployment-info')
      return res.data
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  })

  // Fail-closed on both axes: anything other than an explicit "saas" hides
  // pricing, and an unknown environment is treated as a demo would be only if
  // it says so — an absent environment must not accidentally REVEAL pricing, so
  // it falls back to 'production' and the mode check carries the gate.
  const mode: DeploymentMode =
    data?.deploymentMode === 'saas'
      ? 'saas'
      : data?.deploymentMode === 'on_prem'
        ? 'on_prem'
        : 'self_managed'
  const environment: ProductEnvironment = data?.environment ?? 'production'
  const showFileKeyLicensing = mode !== 'saas'

  return (
    <DeploymentModeContext.Provider
      value={{ mode, environment, showFileKeyLicensing, isLoading }}
    >
      {children}
    </DeploymentModeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDeploymentMode(): DeploymentModeValue {
  return useContext(DeploymentModeContext)
}
